/**
 * EventSelection Component
 * 
 * Second step in the AlertForm wizard that allows users to select specific events to monitor
 * based on the alert type they selected in the previous step.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { 
  AlertType, 
  GlobalEvents, 
  SystemEventEvents, 
  ProcessEvents, 
  LogEvents,
  getEventsForAlertType,
  createAlertRule
} from '../../../models/AlertModels';
import QWebToggle from '../../common/QWebToggle';
import '../../common/QWebToggleStyleLoader';

/**
 * EventSelection component for selecting events to monitor
 * 
 * @returns {JSX.Element} The EventSelection component
 */
function EventSelection() {
  const { formState, updateFormState } = useAlertContext();
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get available events based on the selected alert type
  useEffect(() => {
    const events = getEventsForAlertType(formState.alert.type);
    setAvailableEvents(events);
    
    // Initialize selected events from existing rules if editing
    if (formState.alert.rules && formState.alert.rules.length > 0) {
      const eventNames = formState.alert.rules.map(rule => rule.event);
      setSelectedEvents([...new Set(eventNames)]); // Remove duplicates
    }
  }, [formState.alert.type, formState.alert.rules]);
  
  /**
   * Handle event selection
   * 
   * @param {string} event - The selected event
   */
  const handleEventSelect = (event) => {
    let updatedSelectedEvents;
    
    if (selectedEvents.includes(event)) {
      // Remove event if already selected
      updatedSelectedEvents = selectedEvents.filter(e => e !== event);
    } else {
      // Add event if not selected
      updatedSelectedEvents = [...selectedEvents, event];
    }
    
    setSelectedEvents(updatedSelectedEvents);
    
    // Create or update rules based on selected events
    const updatedRules = updatedSelectedEvents.map(eventName => {
      // Check if a rule for this event already exists
      const existingRule = formState.alert.rules.find(rule => rule.event === eventName);
      
      if (existingRule) {
        return existingRule;
      } else {
        // Create a new rule with default values
        return createAlertRule({ event: eventName });
      }
    });
    
    // Update form state with new rules
    updateFormState({
      alert: {
        ...formState.alert,
        rules: updatedRules
      }
    });
  };
  
  /**
   * Filter events based on search term
   * 
   * @returns {string[]} Filtered events
   */
  const getFilteredEvents = () => {
    if (!searchTerm) return availableEvents;
    
    return availableEvents.filter(event => 
      event.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  /**
   * Get event description based on event name
   * 
   * @param {string} event - Event name
   * @returns {string} Event description
   */
  const getEventDescription = (event) => {
    // Global events
    if (event === 'Websocket.Connected') return 'Triggered when a WebSocket connection is established';
    if (event === 'Websocket.Disconnected') return 'Triggered when a WebSocket connection is lost';
    if (event === 'Server.Connected') return 'Triggered when a server connection is established';
    if (event === 'Server.Disconnected') return 'Triggered when a server connection is lost';
    
    // System events
    if (event === 'cpu.percent') return 'CPU utilization percentage';
    if (event === 'disk.free') return 'Available disk space in GB';
    if (event === 'disk.percent') return 'Disk usage percentage';
    if (event === 'memory.available') return 'Available memory in GB';
    if (event === 'memory.percent') return 'Memory usage percentage';
    // Cumulative network metrics (total since boot)
    if (event === 'network.bytes_sent') return 'Network bytes sent (total)';
    if (event === 'network.bytes_recv') return 'Network bytes received (total)';
    if (event === 'network.packets_sent') return 'Network packets sent (total)';
    if (event === 'network.packets_recv') return 'Network packets received (total)';
    
    // Rate-based network metrics (per second)
    if (event === 'network.bytes_sent_rate') return 'Network bytes sent rate (bytes/s)';
    if (event === 'network.bytes_recv_rate') return 'Network bytes received rate (bytes/s)';
    if (event === 'network.total_bytes_rate') return 'Network total throughput (bytes/s)';
    if (event === 'network.packets_sent_rate') return 'Network packets sent rate (packets/s)';
    if (event === 'network.packets_recv_rate') return 'Network packets received rate (packets/s)';
    if (event === 'network.total_packets_rate') return 'Network total packet rate (packets/s)';
    
    // Process events
    if (event === 'memory_usage') return 'Memory consumption of a process (in GB)';
    if (event === 'cpu_usage') return 'CPU utilization of a process';
    if (event === 'disk_usage') return 'Disk space used by a process (in GB)';
    
    // Log events
    if (event === 'level') return 'Log level (INFO, WARNING, CRITICAL)';
    if (event === 'message') return 'Log message content';
    
    return 'Event description not available';
  };
  
  /**
   * Get event icon based on event name
   * 
   * @param {string} event - Event name
   * @returns {string} Icon class
   */
  const getEventIcon = (event) => {
    // Global events
    if (event.includes('Websocket')) return 'plug';
    if (event.includes('Server')) return 'server';
    
    // System events
    if (event.includes('cpu')) return 'microchip';
    if (event.includes('disk')) return 'hdd-o';
    if (event.includes('memory')) return 'memory';
    if (event.includes('network')) return 'network-wired';
    
    // Process events
    if (event.includes('memory_usage')) return 'memory';
    if (event.includes('cpu_usage')) return 'microchip';
    if (event.includes('disk_usage')) return 'hdd-o';
    
    // Log events
    if (event === 'level') return 'level-up';
    if (event === 'message') return 'comment';
    
    return 'bell';
  };
  
  return (
    <div className="event-selection-container">
      <h3 className="step-title">Select Events to Monitor</h3>
      <p className="step-description">
        Choose one or more events to monitor for your {formState.alert.type} alert.
      </p>
      
      <div className="event-search">
        <input
          type="text"
          placeholder="Search events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="event-search-input"
        />
      </div>
      
      <div className="event-list">
        {getFilteredEvents().length === 0 ? (
          <div className="no-events-message">No events found matching your search.</div>
        ) : (
          getFilteredEvents().map((event) => (
            <div 
              key={event}
              className={`event-item ${selectedEvents.includes(event) ? 'selected' : ''}`}
              onClick={() => handleEventSelect(event)}
            >
              <div className="event-icon">
                <i className={`fa fa-${getEventIcon(event)}`}></i>
              </div>
              <div className="event-details">
                <div className="event-name">{event}</div>
                <div className="event-description">{getEventDescription(event)}</div>
              </div>
              <div className="event-checkbox">
                <QWebToggle
                  checked={selectedEvents.includes(event)}
                  onChange={() => handleEventSelect(event)}
                  showLabel={false}
                  onClick={(e) => e.stopPropagation()}
                  className="event-toggle"
                  aria-label={`Select ${event} event`}
                />
              </div>
            </div>
          ))
        )}
      </div>
      
      {selectedEvents.length === 0 && (
        <div className="event-selection-warning">
          <i className="fa fa-exclamation-triangle"></i>
          Please select at least one event to continue.
        </div>
      )}
    </div>
  );
}

export default EventSelection;
